<template>
  <div>
    <v-text-field
      :label="$t('components.formMCQ.fields.label.name')"
      v-model="question.label"
      :error-messages="labelErrors"
      color="valid"
      tabindex="1"
    />
    <v-textarea
      name="text"
      :label="$t('components.formMCQ.fields.text.name')"
      v-model="question.text"
      :error-messages="textErrors"
      color="valid"
      tabindex="1"
    />

    <v-btn @click="addNewResponse()" color="other3" tabindex="1">
      {{ $t('components.formMCQ.responseButtons.add.name') }}
    </v-btn>

    <ul>
      <draggable v-model="question.responses"
                 group="response"
                 @start="drag=true"
                 @end="drag=false"
      >
        <!-- response.index = Valeur aléatoire pour forcer la recréation -->
        <response v-for="response in question.responses"
                  :key="`${response.index}${response.id}`"
                  :response-init="response"
                  @remove-response="removeResponse"
        />
      </draggable>
    </ul>

    <v-radio-group v-model="question.isUnique"
                   :label="$t('components.formMCQ.fields.isUnique[0].name')"
                   :column="true"
    >
      <v-radio
        v-for="(type, index) in types"
        :key="index"
        :label="type.text"
        :value="type.value"
        color="valid"
        tabindex="1"
      />
    </v-radio-group>
    <div class="tooltip">
      <v-checkbox :label="$t('components.formMCQ.fields.required.name')"
                  v-model="question.required"
                  color="valid"
                  tabindex="1"
      />
      <v-icon :title="$t('components.formMCQ.fields.required.tooltip')"
              class="app__tooltip__cursor__ernest">mdi-information
      </v-icon>
    </div>
    <div class="tooltip">
      <v-checkbox :label="$t('components.formMCQ.fields.isActive.name')"
                  v-model="question.isActive"
                  color="valid"
                  tabindex="1"
      />
      <v-icon :title="$t('components.formMCQ.fields.isActive.tooltip')"
              class="app__tooltip__cursor__ernest">mdi-information
      </v-icon>
    </div>
    <save-buttons
      @save="saveValidation"
      :is-modification="isModification"
      :just-save="justSave"
    />

    <v-dialog v-model="dialogSave.display"
              width="550"
    >
      <v-card>
        <v-card-title>{{ dialogSave.title }}</v-card-title>
        <v-card-text>{{ dialogSave.text }}</v-card-text>
        <v-card-actions>
          <v-btn color="success" @click.stop="saveDialog">
            {{ $t('components.buttons.save')}}
          </v-btn>
          <v-btn color="error" @click.stop="onCancel">
            {{ $t('components.buttons.cancel')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { maxLength, required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import Response from '@/components/questions/formMCQResponses'
import saveButtons from '@/components/questions/saveButtons'

const questionType = 'mcq'

export default {
  name: 'formMCQ',
  components: {
    Response,
    draggable,
    saveButtons
  },
  props: {
    isClone: {
      required: false,
      type: Boolean,
      default: false
    },
    isModification: {
      required: true,
      type: Boolean
    },
    justSave: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialogSave: {
        display: false,
        title: this.$t('components.formMCQ.dialogSave.title.name'),
        text: this.$t('components.formMCQ.dialogSave.text.name'),
        extraAction: '',
        saveType: ''
      },
      newResponseId: 1,
      /**
       * @type {MCQQuestionAir}
       */
      question: {},
      types: [
        {
          text: this.$t('components.formMCQ.fields.isUnique[1].name'),
          value: true
        },
        {
          text: this.$t('components.formMCQ.fields.isUnique[2].name'),
          value: false
        }
      ]
    }
  },
  async created () {
    if (this.isModification || this.isClone) {
      await this.loadQuestion({
        questionType,
        id: this.$route.params.id,
        isClone: this.isClone
      })
    } else {
      this.initQuestion(questionType)
    }

    this.question = this.getDeepCopy()
    this.newResponseId = this.question.responses.length
  },
  computed: {
    ...mapGetters('Questions', [
      'questionMCQ'
    ]),
    labelErrors () {
      const errors = []
      if (!this.$v.question.label.$dirty) return errors
      if (!this.$v.question.label.required) errors.push('Le libellé doit être renseigné.')
      if (!this.$v.question.label.maxLength) errors.push('Le libellé doit avoir moins de 255 caractères.')
      return errors
    },
    textErrors () {
      const errors = []
      if (!this.$v.question.text.$dirty) return errors
      if (!this.$v.question.text.required) errors.push('Le texte de la question doit être renseigné.')
      return errors
    }
  },
  validations: {
    question: {
      label: {
        required,
        maxLength: maxLength(255)
      },
      text: {
        required
      },
      isUnique: {
        required
      },
      required: {
        required
      },
      isActive: {
        required
      }
    }
  },
  methods: {
    ...mapActions('Questions', [
      'initQuestion',
      'loadQuestion'
    ]),
    addNewResponse () {
      this.question.responses.push({
        id: this.newResponseId,
        text: ''
      })

      this.newResponseId++
    },
    getDeepCopy () {
      return {
        ...this.questionMCQ,
        responses: [...this.questionMCQ.responses.map((r, index) => ({ ...r, index }))]
      }
    },
    onCancel () {
      this.dialogSave.display = false
      this.dialogSave.extraAction = ''
      this.dialogSave.saveType = ''
    },
    removeResponse (responseId) {
      this.question.responses = this.question.responses
        .filter(response => responseId !== response.id)

      this.newResponseId = this.question.responses.length
    },
    save (extraAction, saveType) {
      this.question.responses = this.question.responses.map((response, rId) => ({
        ...response,
        id: rId
      }))

      this.$emit('save', {
        question: this.question,
        saveType: saveType
      })

      if (extraAction === 'New') {
        this.$v.$reset()
        this.initQuestion(questionType)
        this.question = this.getDeepCopy()
        this.question.responses = [
          {
            id: '',
            text: ''
          }
        ]
      }
    },
    saveDialog () {
      this.question.isActive = false
      this.save(this.dialogSave.extraAction, this.dialogSave.saveType)
    },
    saveValidation (extraAction = '') {
      this.$v.$touch()
      const saveType = 'save' + extraAction

      if (!this.$v.$invalid) {
        let responses = this.question.responses.filter(response => response.text !== '')

        if (responses.length !== 0) {
          this.save(extraAction, saveType)
        } else {
          this.dialogSave.extraAction = extraAction
          this.dialogSave.saveType = saveType
          this.dialogSave.display = true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../assets/style/styleAppTooltipErnest";

  .tooltip {
    display: flex;
    align-items: center;
    margin: 16px;

    .v-input {
      margin: 0;
    }
  }

  .v-radio {
    margin-left: 20px;
  }
</style>
